/**
 * Sequence Effect Class for Poker Reborn Effects Engine
 *
 * Container effect that plays child effects in sequence (one after another).
 *
 * @see tasks/active/EFFECTS_ENGINE_IMPLEMENTATION_PLAN.md - Phase 2.2.5
 */

import { log } from '@/utils/log';
import type { Effect, EffectOptions } from './Effect';

import { EffectType, EffectState, EffectPriority } from './EffectTypes';
import { BaseEffect } from './BaseEffect';

// =============================================================================
// SEQUENCE EFFECT CLASS (P2.2.5)
// =============================================================================

/**
 * Plays child effects in sequence (one after another).
 *
 * Example:
 * ```
 * new SequenceEffect([
 *   new CardDealEffect(seat1),
 *   new CardDealEffect(seat2),
 *   new CardDealEffect(seat3),
 * ])
 * ```
 */
export class SequenceEffect extends BaseEffect {
  private readonly _effects: Effect[];
  private _currentIndex = 0;
  private _currentEffect: Effect | null = null;

  constructor(
    effects: Effect[],
    options: Omit<EffectOptions, 'duration'> = {}
  ) {
    // Calculate total duration
    const totalDuration = effects.reduce((sum, e) => sum + e.duration, 0);

    // Use the highest priority among children, or NORMAL
    const maxPriority = effects.length > 0
      ? Math.max(...effects.map((e) => e.priority))
      : EffectPriority.NORMAL;

    super('sequence', EffectType.SEQUENCE, { type: 'global' }, {
      ...options,
      duration: totalDuration,
      priority: options.priority ?? maxPriority,
    });

    this._effects = effects;
  }

  /**
   * Get the child effects.
   */
  get effects(): ReadonlyArray<Effect> {
    return this._effects;
  }

  /**
   * Get current child index.
   */
  get currentIndex(): number {
    return this._currentIndex;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle Implementation
  // ---------------------------------------------------------------------------

  protected onPlay(): void {
    this.playNext();
  }

  protected onCancel(): void {
    // Cancel current effect if any
    if (this._currentEffect) {
      this._currentEffect.cancel();
      this._currentEffect = null;
    }

    // Cancel all remaining effects
    for (let i = this._currentIndex + 1; i < this._effects.length; i++) {
      const effect = this._effects[i];
      if (effect && effect.cancellable) {
        effect.cancel();
      }
    }
  }

  protected override onPause(): void {
    if (this._currentEffect?.pausable) {
      this._currentEffect.pause();
    }
  }

  protected override onResume(): void {
    if (this._currentEffect?.state === EffectState.PAUSED) {
      this._currentEffect.resume();
    }
  }

  // ---------------------------------------------------------------------------
  // Private Methods
  // ---------------------------------------------------------------------------

  private async playNext(): Promise<void> {
    // Check if we're done or cancelled
    if (this._currentIndex >= this._effects.length) {
      this.complete();
      return;
    }

    if (this._state !== EffectState.PLAYING) {
      return;
    }

    const effect = this._effects[this._currentIndex];
    if (!effect) {
      this._currentIndex++;
      this.playNext();
      return;
    }

    this._currentEffect = effect;

    log.effects.debug`SequenceEffect: playing child ${this._currentIndex + 1}/${this._effects.length}`;

    try {
      await effect.play();

      // Move to next
      this._currentIndex++;
      this._currentEffect = null;

      // Update progress
      const progress = this._currentIndex / this._effects.length;
      this.updateProgress(progress);

      // Play next (if not cancelled)
      if (this._state === EffectState.PLAYING) {
        this.playNext();
      }
    } catch (error) {
      log.effects.error`SequenceEffect child error: ${error}`;
      this.completeWithError(error as Error);
    }
  }

  // ---------------------------------------------------------------------------
  // Conflict Detection Override
  // ---------------------------------------------------------------------------

  override conflictsWith(other: Effect): boolean {
    // A sequence conflicts if any of its children conflict
    return this._effects.some((e) => e.conflictsWith(other));
  }
}

// =============================================================================
// FACTORY HELPER
// =============================================================================

/**
 * Create a sequence of effects with optional delays between them.
 */
export function createSequence(
  effects: Effect[],
  options: Omit<EffectOptions, 'duration'> = {}
): SequenceEffect {
  return new SequenceEffect(effects, options);
}
